import {Injectable} from '@angular/core';
import {EnumTablasIndexedDB} from "../models/enum/enum-indexed-db";
import {ClsRespuestaFechasConsultas} from '../models/peticiones/cls-pr-generico';
import {ClsCatalogosExpIns} from '../models/cls-catalogo-general';
import {ClsMatriz} from '../models/cls-matriz';
import {ClsBusquedaAIFavorito} from '../models/cls-busqueda-ai';

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {
  private nombreDB: string = 'DatosNeo';
  private versionDB: number = 2;
  // 👇 Aquí defines todos los stores que tendrá tu BD
  private stores: { name: string; keyPath: string }[] = [
    { name: EnumTablasIndexedDB.DESCONEXION, keyPath: "id" },
    { name: EnumTablasIndexedDB.REDLENTA, keyPath: "id" },
    { name: EnumTablasIndexedDB.FECHAS_CONSULTAS, keyPath: 'id' },
    { name: EnumTablasIndexedDB.CATALOGO_AI, keyPath: 'id' },
    { name: EnumTablasIndexedDB.FAVORITOS_AI, keyPath: 'id' }
  ];

  /**
   * Crea o abre una base y store si no existen
   */
  private async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.nombreDB, this.versionDB);

      request.onupgradeneeded = () => {
        const db = request.result;
        this.stores.forEach(store => {
          if (!db.objectStoreNames.contains(store.name)) {
            db.createObjectStore(store.name, { keyPath: store.keyPath , autoIncrement: true });
          }
        });
      };

      request.onsuccess = () => resolve(request.result);

      request.onerror = () => reject(request.error);
    });
  }

  /** ✅ Guardar nuevo dato */
  public async guardarDato<T>(storeName: string, data: T): Promise<void> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).add(data);

      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  /** 🔄 Actualizar dato por clave */
  public async actualizarDato<T>(storeName: string, data: T): Promise<void> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).put(data); // put reemplaza o inserta

      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  /** ❌ Eliminar por clave */
  public async eliminarPorClave(storeName: string, key: IDBValidKey): Promise<void> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).delete(key);

      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  /** 📄 Obtener uno por clave */
  public async obtenerPorClave<T>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).get(key);

      req.onsuccess = () => {
        db.close();
        resolve(req.result as T);
      };
      req.onerror = () => reject(req.error);
    });
  }

  /** 📦 Obtener todos */
  public async obtenerTodos<T>(storeName: string): Promise<T[]> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const getAllRequest = tx.objectStore(storeName).getAll();

      getAllRequest.onsuccess = () => {
        db.close();
        resolve(getAllRequest.result as T[]);
      };

      getAllRequest.onerror = () => reject(getAllRequest.error);
    });
  }

  /** 🗑️ Eliminar completamente la base de datos */
  public async resetDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase(this.nombreDB);

      deleteRequest.onsuccess = () => {
        console.log(`Base de datos '${this.nombreDB}' eliminada correctamente.`);
        resolve();
      };

      deleteRequest.onerror = () => {
        console.error("Error eliminando la base de datos:", deleteRequest.error);
        reject(deleteRequest.error);
      };

      deleteRequest.onblocked = () => {
        console.warn("La base de datos está en uso. Cierra pestañas abiertas con esta app e inténtalo de nuevo.");
      };
    });
  }

  // ====== Funciones específicas para fechas de catálogo/matrices (Busqueda AI) ======
  public async obtenerFechasConsultas(): Promise<ClsRespuestaFechasConsultas | null> {
    try {
      const result = await this.obtenerPorClave<ClsRespuestaFechasConsultas>(
        EnumTablasIndexedDB.FECHAS_CONSULTAS,
        1
      );
      return result ?? null;
    } catch {
      return null;
    }
  }

  public async guardarFechasConsultas(fechas: ClsRespuestaFechasConsultas): Promise<void> {
    const data = {id: 1, FechaCatalogo: fechas.FechaCatalogo, FechaMatrices: fechas.FechaMatrices};
    await this.actualizarDato(EnumTablasIndexedDB.FECHAS_CONSULTAS, data);
  }

  // ====== Funciones específicas para catálogo/matrices AI ======
  public async guardarDatosBusquedaAI(catalogos: ClsCatalogosExpIns[], matrices: ClsMatriz[]): Promise<void> {
    const data = {id: 1, catalogos, matrices};
    await this.actualizarDato(EnumTablasIndexedDB.CATALOGO_AI, data);
  }

  public async obtenerDatosBusquedaAI(): Promise<{ catalogos: ClsCatalogosExpIns[]; matrices: ClsMatriz[] }> {
    try {
      const result = await this.obtenerPorClave<{
        id: number;
        catalogos: ClsCatalogosExpIns[];
        matrices: ClsMatriz[];
      }>(EnumTablasIndexedDB.CATALOGO_AI, 1);
      return {
        catalogos: result?.catalogos ?? [],
        matrices: result?.matrices ?? []
      };
    } catch {
      return {catalogos: [], matrices: []};
    }
  }

  public async guardarFavoritosBusquedaAI(items: ClsBusquedaAIFavorito[]): Promise<void> {
    const data = {id: 1, items};
    await this.actualizarDato(EnumTablasIndexedDB.FAVORITOS_AI, data);
  }

  public async obtenerFavoritosBusquedaAI(): Promise<ClsBusquedaAIFavorito[]> {
    try {
      const result = await this.obtenerPorClave<{
        id: number;
        items: ClsBusquedaAIFavorito[];
      }>(EnumTablasIndexedDB.FAVORITOS_AI, 1);
      return result?.items ?? [];
    } catch {
      return [];
    }
  }
}
